import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Card, Typography, Select, Option, Input, Table, Chip, Button, Stack, Menu, MenuItem, Divider } from '@mui/joy';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { adapterService, type UnifiedDoc } from '../../docs/services/adapter.service';
import { documentsService } from '../services/documents.service';
import type { DocumentStatus } from '../types/documents.types';
import { getAvailableStatuses } from '../types/documents.types';
import { invoiceDisplayStatus } from '../utils/invoiceLedger';
import { sendDocumentToCustomer } from '../services/sendDocumentToCustomer.service';
import { ensureSignedIn, GRAPH_SIGNIN_REQUIRED_TO_SEND } from '../../../utils/graphService';
import {
  isWorkspaceDocument,
  WORKSPACE_DOCUMENT_READ_ONLY_MESSAGE,
  workspaceDocumentPath,
} from '../utils/workspaceDocumentAccess';

type KindFilter = 'all' | 'quote_estimate' | 'Estimate' | 'Quotation' | 'Invoice';
type StatusFilter = 'all' | 'unpaid' | 'draft' | 'sent' | 'accepted' | 'rejected' | 'invoiced' | 'paid' | 'cash' | 'void';
type PipelineStateFilter = 'all' | 'awaiting_final';

const formatCurrency = (v?: number) => (v == null ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v));
const normalizeStatus = (status?: string) => String(status || '').trim().toLowerCase();
const formatStatusLabel = (status?: string) => {
  const normalized = normalizeStatus(status);
  if (!normalized) return '—';
  if (normalized === 'cash') return 'Cash';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};
const getStatusChipColor = (status?: string): 'neutral' | 'primary' | 'success' | 'warning' | 'danger' => {
  const normalized = normalizeStatus(status);
  if (normalized === 'paid' || normalized === 'accepted') return 'success';
  if (normalized === 'cash') return 'danger';
  if (normalized === 'rejected' || normalized === 'void') return 'danger';
  if (normalized === 'sent' || normalized === 'invoiced') return 'warning';
  return 'neutral';
};

function rowStatusChip(row: UnifiedDoc): { label: string; color: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' } {
  if (row.kind === 'Invoice') return invoiceDisplayStatus({ status: row.status });
  return { label: formatStatusLabel(row.status), color: getStatusChipColor(row.status) };
}

export const DocumentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<UnifiedDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getKindFromQuery = useCallback((raw: string | null): KindFilter => {
    if (raw === 'Estimate' || raw === 'Quotation' || raw === 'Invoice' || raw === 'all' || raw === 'quote_estimate') return raw;
    return 'all';
  }, []);
  const getStatusFromQuery = useCallback((raw: string | null): StatusFilter => {
    const allowed: StatusFilter[] = ['all', 'unpaid', 'draft', 'sent', 'accepted', 'rejected', 'invoiced', 'paid', 'cash', 'void'];
    return (allowed as string[]).includes(String(raw)) ? (raw as StatusFilter) : 'all';
  }, []);
  const getCreatedAfterFromQuery = useCallback((raw: string | null): string | undefined => {
    if (!raw) return undefined;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return undefined;
    return parsed.toISOString();
  }, []);
  const getCreatedBeforeFromQuery = useCallback((raw: string | null): string | undefined => {
    if (!raw) return undefined;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return undefined;
    return parsed.toISOString();
  }, []);
  const getInputVatSelectedFromQuery = useCallback((raw: string | null): boolean | undefined => {
    if (raw == null) return undefined;
    const normalized = String(raw).trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
    return undefined;
  }, []);
  const getPipelineStateFromQuery = useCallback((raw: string | null): PipelineStateFilter => {
    if (raw === 'awaiting_final') return 'awaiting_final';
    return 'all';
  }, []);
  const [kind, setKind] = useState<KindFilter>(() => getKindFromQuery(searchParams.get('kind')));
  const [status, setStatus] = useState<StatusFilter>(() => getStatusFromQuery(searchParams.get('status')));
  const [search, setSearch] = useState('');
  const [statusMenuId, setStatusMenuId] = useState<string | null>(null);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [savingStatusId, setSavingStatusId] = useState<string | null>(null);
  const [sendingDocId, setSendingDocId] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const itemsPerPage = 20;
  const prevFilterKeyRef = useRef('');
  const loadGenerationRef = useRef(0);

  const createdAfter = getCreatedAfterFromQuery(searchParams.get('from'));
  const createdBefore = getCreatedBeforeFromQuery(searchParams.get('to'));
  const invoiceStateFilter = searchParams.get('invoice_state');
  const inputVatSelectedFilter = getInputVatSelectedFromQuery(searchParams.get('input_vat_selected'));
  const pipelineStateFilter = getPipelineStateFromQuery(searchParams.get('pipeline_state'));

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const listFilterKey = [
    kind,
    status,
    debouncedSearch,
    createdAfter || '',
    createdBefore || '',
    invoiceStateFilter || '',
    String(inputVatSelectedFilter ?? ''),
    pipelineStateFilter,
  ].join('|');

  const loadDocuments = useCallback(async (page: number) => {
    const generation = ++loadGenerationRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await adapterService.getUnifiedDocuments({
        source: 'jarvis',
        kind,
        status,
        search: debouncedSearch,
        createdAfter,
        createdBefore,
        inputVatSelected: inputVatSelectedFilter,
        invoiceState: invoiceStateFilter,
        pipelineState: pipelineStateFilter,
        page,
        pageSize: itemsPerPage,
      });
      if (generation !== loadGenerationRef.current) return;
      setItems(result.items);
      setTotalItems(result.total);
      const lastPage = Math.max(1, Math.ceil(result.total / itemsPerPage) || 1);
      if (page > lastPage) {
        setCurrentPage(lastPage);
      }
    } catch (e: any) {
      if (generation !== loadGenerationRef.current) return;
      setError(e?.message || 'Failed to load documents');
      setItems([]);
      setTotalItems(0);
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [kind, status, debouncedSearch, createdAfter, createdBefore, inputVatSelectedFilter, invoiceStateFilter, pipelineStateFilter]);

  const refreshData = async () => {
    await loadDocuments(currentPage);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + items.length;

  // Apply query-string filters when arriving from deep links (e.g. dashboard widgets)
  useEffect(() => {
    setKind(getKindFromQuery(searchParams.get('kind')));
    setStatus(getStatusFromQuery(searchParams.get('status')));
  }, [searchParams, getKindFromQuery, getStatusFromQuery]);

  useEffect(() => {
    if (prevFilterKeyRef.current !== listFilterKey) {
      prevFilterKeyRef.current = listFilterKey;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    void loadDocuments(currentPage);
  }, [listFilterKey, currentPage, loadDocuments]);


  const filterFieldSx = {
    minWidth: { xs: '100%', sm: 160 },
    flex: '1 1 160px',
    borderRadius: { xs: '0.25rem', sm: '0.75rem' },
    px: { xs: '0.375rem', sm: '0.75rem' },
    boxShadow: { xs: 'none', sm: 'sm' },
    border: { xs: '1px solid var(--joy-palette-neutral-200)', sm: '1px solid var(--joy-palette-neutral-outlinedBorder)' },
    backgroundColor: { xs: 'transparent', sm: 'var(--joy-palette-background-surface)' },
    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: 1400,
      mx: 'auto',
      px: { xs: 1, sm: 2 }
    }}>
      <Typography level="h3" sx={{ mb: 2 }}>Documents</Typography>
      {/* Mobile: Simple Box (no card styling), Desktop: Card */}
      <Box
        sx={{
          p: { xs: 0, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
          boxShadow: { xs: 'none', sm: 'sm' },
          border: { xs: 'none', sm: '1px solid var(--joy-palette-neutral-outlinedBorder)' },
          borderRadius: { xs: 0, sm: 'var(--Card-radius, 12px)' },
          backgroundColor: { xs: 'transparent', sm: 'var(--joy-palette-background-surface)' },
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={{ xs: 0.75, sm: 1.5 }} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ flexWrap: 'wrap', rowGap: { xs: 0.75, sm: 1.5 }, columnGap: 1.5 }}
        >
          <Select
            value={kind}
            onChange={(_, v) => v && setKind(v as KindFilter)}
            sx={{
              ...filterFieldSx,
              '@media (max-width: 600px)': {
                height: '28px !important',
                minHeight: '28px !important',
                maxHeight: '28px !important',
                '& button': {
                  height: '28px !important',
                  minHeight: '28px !important',
                  maxHeight: '28px !important',
                  padding: '0.25rem 0.375rem !important',
                  fontSize: '0.875rem !important',
                  lineHeight: '1.2 !important',
                },
                '& .MuiSelect-button': {
                  height: '28px !important',
                  minHeight: '28px !important',
                  maxHeight: '28px !important',
                }
              }
            }}
          >
            <Option value="all">All Kinds</Option>
            <Option value="quote_estimate">Quotes + Estimates</Option>
            <Option value="Estimate">Estimates</Option>
            <Option value="Quotation">Quotations</Option>
            <Option value="Invoice">Invoices</Option>
          </Select>
          <Select
            value={status}
            onChange={(_, v) => v && setStatus(v as StatusFilter)}
            sx={{
              ...filterFieldSx,
              '@media (max-width: 600px)': {
                height: '28px !important',
                minHeight: '28px !important',
                maxHeight: '28px !important',
                '& button': {
                  height: '28px !important',
                  minHeight: '28px !important',
                  maxHeight: '28px !important',
                  padding: '0.25rem 0.375rem !important',
                  fontSize: '0.875rem !important',
                  lineHeight: '1.2 !important',
                },
                '& .MuiSelect-button': {
                  height: '28px !important',
                  minHeight: '28px !important',
                  maxHeight: '28px !important',
                }
              }
            }}
          >
            <Option value="all">All Statuses</Option>
            <Option value="unpaid">Unpaid (Sent + Draft)</Option>
            <Option value="draft">Draft</Option>
            <Option value="sent">Sent</Option>
            <Option value="accepted">Accepted</Option>
            <Option value="rejected">Rejected</Option>
            <Option value="invoiced">Invoiced</Option>
            <Option value="paid">Paid</Option>
            <Option value="cash">Cash</Option>
            <Option value="void">Void</Option>
          </Select>
          <Input 
            placeholder="Search ref/title/customer/status..." 
            value={search} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} 
            sx={{
              ...filterFieldSx,
              minWidth: { xs: '100%', sm: 220 },
              flex: '2 1 220px',
              '@media (max-width: 600px)': {
                height: '28px !important',
                minHeight: '28px !important',
                maxHeight: '28px !important',
                '& input': {
                  height: '28px !important',
                  minHeight: '28px !important',
                  maxHeight: '28px !important',
                  padding: '0.25rem 0.375rem !important',
                  fontSize: '0.875rem !important',
                  lineHeight: '1.2 !important',
                },
                '& .MuiInput-input': {
                  height: '28px !important',
                  minHeight: '28px !important',
                  maxHeight: '28px !important',
                }
              }
            }}
          />
          <Box sx={{ display: { xs: 'none', sm: 'block' }, flex: '1 1 120px' }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 1 }} sx={{ flex: '0 0 auto', width: { xs: '100%', sm: 'auto' } }}>
            <Button 
              size="sm" 
              variant="solid" 
              onClick={() => navigate('/docs/new?kind=quotation')} 
              sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: { xs: '28px', sm: 'auto' } }}
            >
              New Estimate/Quote
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && <Typography color="danger" sx={{ mb: 1 }}>{error}</Typography>}

      <Card sx={{ width: '100%' }}>
        {/* Desktop Table View */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
          <Table hoverRow sx={{ minWidth: 1180 }}>
            <thead>
              <tr>
                <th style={{ minWidth: '80px' }}>Source</th>
                <th style={{ minWidth: '80px' }}>Kind</th>
                <th style={{ minWidth: '100px' }}>Ref</th>
                <th style={{ minWidth: '200px' }}>Title</th>
                <th style={{ minWidth: '150px' }}>Customer</th>
                <th style={{ minWidth: '80px' }}>Status</th>
                <th style={{ minWidth: '100px' }}>Total</th>
                <th style={{ minWidth: '110px' }}>Document_Date</th>
                <th style={{ minWidth: '100px' }}>Created</th>
                <th style={{ minWidth: '180px' }}>Actions</th>
              </tr>
            </thead>
          <tbody>
            {(items || []).map(row => (
              <tr key={`${row.source}-${row.documentId || row.lifecycleRef}-${row.refNo}`}>
                <td>
                  <Chip size="sm" color="primary">JARVIS</Chip>
                </td>
                <td>
                  <Chip size="sm" variant="soft" color={row.kind === 'Estimate' ? 'primary' : row.kind === 'Quotation' ? 'success' : 'warning'}>
                    {row.kind}
                  </Chip>
                </td>
                <td>
                  {row.documentId ? (
                    <Link to={`/docs/${row.documentId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      {row.refNo || row.lifecycleRef}
                    </Link>
                  ) : (
                    row.refNo || row.lifecycleRef
                  )}
                </td>
                <td>
                  {row.documentId ? (
                    <Link to={`/docs/${row.documentId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      {row.title}
                    </Link>
                  ) : (
                    row.title
                  )}
                </td>
                <td>
                  {row.documentId ? (
                    <Link to={`/docs/${row.documentId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      {row.customerName || '—'}
                    </Link>
                  ) : (
                    row.customerName || '—'
                  )}
                </td>
                <td>
                  {row.documentId && !isWorkspaceDocument(row) ? (
                    <>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={rowStatusChip(row).color}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          const isOpen = statusMenuId === row.documentId;
                          if (isOpen) {
                            setStatusMenuId(null);
                            setStatusAnchor(null);
                          } else {
                            setStatusMenuId(row.documentId!);
                            setStatusAnchor(e.currentTarget as HTMLElement);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {rowStatusChip(row).label}
                      </Chip>
                      <Menu
                        open={statusMenuId === row.documentId}
                        anchorEl={statusAnchor}
                        onClose={() => { setStatusMenuId(null); setStatusAnchor(null); }}
                      >
                        {getAvailableStatuses(row.kind).map(s => (
                            <MenuItem
                              key={s}
                              selected={s === String(row.status || '').toLowerCase()}
                              onClick={async () => {
                                try {
                                  setSavingStatusId(row.documentId!);
                                  await documentsService.updateDocument(row.documentId!, { status: s });
                                  // Auto-create invoice when Estimate/Quote status changes to 'accepted'
                                  if ((row.kind === 'Quotation' || row.kind === 'Estimate') && s === 'accepted') {
                                    try {
                                      const created = await documentsService.ensureDepositInvoice(row.documentId!);
                                      window.alert(created ? 'Deposit invoice created.' : 'No deposit is configured, so no deposit invoice was created.');
                                    } catch (err: any) {
                                      console.error('Auto-create invoice failed:', err);
                                      window.alert(`Failed to auto-create invoice: ${err?.message || 'Unknown error'}`);
                                    }
                                  }
                                  await refreshData();
                                } catch (e: any) {
                                  console.error('Failed to update status', e);
                                  alert('Failed to update status. Please try again.');
                                } finally {
                                  setSavingStatusId(null);
                                  setStatusMenuId(null);
                                  setStatusAnchor(null);
                                }
                              }}
                            >
                              {formatStatusLabel(s)}
                            </MenuItem>
                          ))}
                      </Menu>
                    </>
                  ) : (
                    row.status ? (
                      <Chip size="sm" variant="soft" color={rowStatusChip(row).color}>
                        {rowStatusChip(row).label}
                      </Chip>
                    ) : '—'
                  )}
                </td>
                <td
                  onClick={() => {
                    if (row.documentId) navigate(`/docs/${row.documentId}`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {formatCurrency(row.total)}
                </td>
                <td
                  onClick={() => {
                    if (row.documentId) navigate(`/docs/${row.documentId}`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {row.issuedAt
                    ? new Date(row.issuedAt).toLocaleDateString('en-GB')
                    : '—'}
                </td>
                <td
                  onClick={() => {
                    if (row.documentId) navigate(`/docs/${row.documentId}`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB') : '—'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {row.documentId && isWorkspaceDocument(row) && (
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        size="sm"
                        variant="outlined"
                        title={WORKSPACE_DOCUMENT_READ_ONLY_MESSAGE}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          const path = workspaceDocumentPath({ id: row.documentId, kind: row.kind, projectId: row.projectId });
                          if (path) navigate(path);
                        }}
                      >
                        Open workspace
                      </Button>
                    </Stack>
                  )}
                  {row.documentId && !isWorkspaceDocument(row) && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: 'nowrap' }}>
                      {(row.kind === 'Estimate' || row.kind === 'Quotation' || row.kind === 'Invoice') && (
                        <Button
                          size="sm"
                          variant="outlined"
                          disabled={!!sendingDocId && sendingDocId === row.documentId}
                          loading={sendingDocId === row.documentId}
                          sx={{ minWidth: 72 }}
                          onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            try {
                              const ready = await ensureSignedIn();
                              if (!ready) {
                                alert(GRAPH_SIGNIN_REQUIRED_TO_SEND);
                                return;
                              }
                              const ok = window.confirm(`Send this ${row.kind.toLowerCase()} to the customer?`);
                              if (!ok) return;
                              setSendingDocId(row.documentId!);
                              await sendDocumentToCustomer(row.documentId!);
                              await refreshData();
                              alert('Sent successfully.');
                            } catch (err: any) {
                              console.error('Failed to send document:', err);
                              alert(`Failed to send: ${err?.message || 'Unknown error'}`);
                            } finally {
                              setSendingDocId(null);
                            }
                          }}
                        >
                          Send
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        sx={{ minWidth: 72 }}
                        onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          const confirmed = window.confirm(`Are you sure you want to delete this ${row.kind}? This action cannot be undone.`);
                          if (!confirmed) return;
                          
                          try {
                            await documentsService.deleteDocument(row.documentId!);
                            alert(`${row.kind.charAt(0).toUpperCase() + row.kind.slice(1)} deleted successfully.`);
                            // Refresh the list
                            await refreshData();
                          } catch (error: any) {
                            console.error('Error deleting document:', error);
                            alert('Failed to delete document. Please try again.');
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        </Box>

        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Stack spacing={2}>
            {(items || []).map(row => (
              <Card
                key={`${row.source}-${row.documentId || row.lifecycleRef}-${row.refNo}`}
                variant="outlined"
                sx={{
                  p: 1.5,
                  boxShadow: 'sm',
                  borderColor: 'neutral.outlinedBorder',
                }}
              >
                <Stack spacing={1.5}>
                  <Stack spacing={0.5}>
                    <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
                      {row.title || row.lifecycleRef}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {row.refNo || row.lifecycleRef} • {row.kind}
                    </Typography>
                  </Stack>

                  {row.customerName && (
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.25 }}>
                        Customer
                      </Typography>
                      <Typography level="body-sm">{row.customerName}</Typography>
                    </Box>
                  )}

                  <Stack spacing={0.5}>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      Status • {formatStatusLabel(String(row.status))}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      Created • {row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      Document_Date • {row.issuedAt ? new Date(row.issuedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </Typography>
                  </Stack>

                  <Divider />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        Total
                      </Typography>
                      <Typography level="body-sm" fontWeight="lg">
                        {formatCurrency(row.total)}
                      </Typography>
                    </Box>
                    {row.documentId && (
                      <Button
                        size="sm"
                        variant="plain"
                        onClick={() => navigate(`/docs/${row.documentId}`)}
                      >
                        Open
                      </Button>
                    )}
                  </Stack>

                  {row.documentId && isWorkspaceDocument(row) && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="sm"
                        variant="outlined"
                        fullWidth
                        onClick={() => navigate(`/docs/${row.documentId}`)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          const path = workspaceDocumentPath({ id: row.documentId, kind: row.kind, projectId: row.projectId });
                          if (path) navigate(path);
                        }}
                      >
                        Open workspace
                      </Button>
                    </Stack>
                  )}
                  {row.documentId && !isWorkspaceDocument(row) && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="sm"
                        variant="outlined"
                        fullWidth
                        onClick={() => navigate(`/docs/${row.documentId}`)}
                      >
                        View
                      </Button>
                      {(row.kind === 'Estimate' || row.kind === 'Quotation') && (
                        <Button
                          size="sm"
                          variant="outlined"
                          fullWidth
                          loading={sendingDocId === row.documentId}
                          disabled={sendingDocId === row.documentId}
                          onClick={async () => {
                            try {
                              const ready = await ensureSignedIn();
                              if (!ready) {
                                alert(GRAPH_SIGNIN_REQUIRED_TO_SEND);
                                return;
                              }
                              const ok = window.confirm(`Send this ${row.kind.toLowerCase()} to the customer?`);
                              if (!ok) return;
                              setSendingDocId(row.documentId!);
                              await sendDocumentToCustomer(row.documentId!);
                              await refreshData();
                              alert('Sent successfully.');
                            } catch (err: any) {
                              console.error('Failed to send document:', err);
                              alert(`Failed to send: ${err?.message || 'Unknown error'}`);
                            } finally {
                              setSendingDocId(null);
                            }
                          }}
                        >
                          Send
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        fullWidth
                        onClick={async () => {
                          const confirmed = window.confirm(`Delete ${row.kind}? This cannot be undone.`);
                          if (!confirmed) return;
                          try {
                            await documentsService.deleteDocument(row.documentId!);
                            alert(`${row.kind.charAt(0).toUpperCase() + row.kind.slice(1)} deleted successfully.`);
                            await refreshData();
                          } catch (error: any) {
                            console.error('Error deleting document:', error);
                            alert('Failed to delete document. Please try again.');
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        </Box>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2, px: { xs: 1, sm: 0 } }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={1} 
              alignItems="center"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button 
                  size="sm" 
                  variant="outlined" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  First
                </Button>
                <Button 
                  size="sm" 
                  variant="outlined" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Previous
                </Button>
              </Stack>
              <Typography level="body-sm" sx={{ px: { xs: 0, sm: 2 }, textAlign: 'center' }}>
                Page {currentPage} of {totalPages}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button 
                  size="sm" 
                  variant="outlined" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Next
                </Button>
                <Button 
                  size="sm" 
                  variant="outlined" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Last
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
        
        {/* Results summary */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 1 }}>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            {totalItems === 0
              ? '0 documents'
              : `Showing ${startIndex + 1}-${endIndex} of ${totalItems} documents`}
          </Typography>
          {debouncedSearch && (
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              Matching “{debouncedSearch}”
            </Typography>
          )}
        </Box>
        
        {loading && <Typography level="body-sm" sx={{ p: 2 }}>Loading…</Typography>}
        {!loading && items.length === 0 && <Typography level="body-sm" sx={{ p: 2 }}>No documents found.</Typography>}
      </Card>

    </Box>
  );
};

export default DocumentsListPage;


